import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import type * as React from "react";

import {
	SegmentedControl as CrowSegmentedControl,
	SegmentedControlItem as CrowSegmentedControlItem,
} from "@crow/ui/segmented-control";
import { cn } from "../../lib/utils";

/**
 * A row of mutually exclusive choices in one recessed track (the charter's segmented control: Browse /
 * Installed / Custom, System / Light / Dark, Me / My organization). One choice is always on. The
 * design system's (`design-system/ui`) behaviour, on Crowdex's density-aware sizes.
 */
function SegmentedControl({
	className,
	...props
}: React.ComponentProps<typeof CrowSegmentedControl<string>>) {
	return (
		<CrowSegmentedControl
			data-slot="segmented-control"
			className={cn("rounded-lg bg-field p-1", className)}
			{...props}
		/>
	);
}

/**
 * The chosen segment: `SELECTED_CONTROL` keyed on Radix's `data-state="on"`, written out so
 * Tailwind sees the classes (chip.test.tsx keeps the two in step). The track is recessed
 * (`bg-field`) so the chosen segment reads as the control lifted out of it.
 */
export const SEGMENT_SELECTED =
	"data-[state=on]:border-input data-[state=on]:bg-control data-[state=on]:text-foreground";

/** A segment shares the control's free width and is never narrower than its label. */
function SegmentedControlItem({
	className,
	...props
}: React.ComponentProps<typeof CrowSegmentedControlItem>) {
	return (
		<CrowSegmentedControlItem
			data-slot="segmented-control-item"
			className={cn(
				"h-control-sm flex-1 rounded-control border border-transparent px-3.5 text-muted-foreground type-ui hover:text-foreground data-[state=on]:shadow-none",
				SEGMENT_SELECTED,
				className,
			)}
			{...props}
		/>
	);
}

/**
 * The same single choice, unstyled: swatches, theme pictures, the cards of a dialog. Arrow keys
 * move between the choices; each is a radio for assistive technologies.
 */
function ChoiceGroup({
	value,
	onValueChange,
	...props
}: Omit<
	React.ComponentProps<typeof ToggleGroupPrimitive.Root>,
	"type" | "value" | "defaultValue" | "onValueChange"
> & {
	value: string;
	onValueChange: (value: string) => void;
}) {
	return (
		<ToggleGroupPrimitive.Root
			data-slot="choice-group"
			onValueChange={(next) => {
				if (next) onValueChange(next);
			}}
			type="single"
			value={value}
			{...props}
		/>
	);
}

function ChoiceItem(props: React.ComponentProps<typeof ToggleGroupPrimitive.Item>) {
	return <ToggleGroupPrimitive.Item data-slot="choice-item" {...props} />;
}

export { ChoiceGroup, ChoiceItem, SegmentedControl, SegmentedControlItem };
